import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllPromptsAsync, editPromptAsync } from "../redux/prompt";
import { useFooter } from "../components/layout/FooterContext";
import AppButtonFooter from "../components/common/AppButtonFooter";

const Prompts = () => {
  const dispatch = useDispatch();
  const prompts = useSelector((state) => state.prompt?.prompts ?? []);
  const loading = useSelector((state) => state.prompt?.loading ?? false);
  const error = useSelector((state) => state.prompt?.error ?? null);

  const { setFooterContent, setTitle } = useFooter();

  const [editedById, setEditedById] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    dispatch(getAllPromptsAsync());
  }, [dispatch]);

  // Select first prompt once loaded
  useEffect(() => {
    if (prompts.length > 0 && selectedId === null) {
      setSelectedId(prompts[0].id);
    }
  }, [prompts, selectedId]);

  const isDirty = useMemo(() => Object.keys(editedById).length > 0, [editedById]);

  const handleSaveAll = useCallback(async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const ops = Object.entries(editedById).map(([id, content]) =>
        dispatch(editPromptAsync({ id: Number(id), payload: { content } })).unwrap()
      );
      await Promise.all(ops);
      setEditedById({});
    } catch (e) {
      console.error("Failed to save prompts:", e);
    } finally {
      setSaving(false);
    }
  }, [dispatch, editedById, isDirty, saving]);

  useEffect(() => {
    setFooterContent(
      <footer className="flex justify-end gap-2">
        <AppButtonFooter
          onClick={handleSaveAll}
          width="w-[120px]"
          disabled={loading || !isDirty}
        >
          {saving ? "Saving..." : "Speichern"}
        </AppButtonFooter>
      </footer>
    );
    setTitle("Prompts");
    return () => setFooterContent(null);
  }, [handleSaveAll, loading, isDirty, saving, setFooterContent, setTitle]);

  useEffect(() => {
    setEditedById((prev) => {
      const next = { ...prev };
      prompts.forEach((p) => {
        if (String(p.id) in next && next[p.id] === p.content) delete next[p.id];
      });
      return next;
    });
  }, [prompts]);

  const handleChange = (id) => (e) => {
    const value = e.target.value;
    const original = prompts.find((p) => p.id === id)?.content ?? "";
    setEditedById((prev) => {
      if (value === original) {
        const { [id]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: value };
    });
  };

  const selectedPrompt = prompts.find((p) => p.id === selectedId) ?? null;

  return (
    <div className="px-4">
      <div className="h-[82vh] bg-surface rounded-2xl p-3">
        <div className="flex flex-row gap-4 h-full min-h-0">

          {/* Left Panel — prompt list */}
          <div className="w-64 shrink-0 flex flex-col gap-1 overflow-y-auto">
            <p className="text-xs text-default-400 uppercase font-semibold px-3 mb-1">
              Prompts
            </p>
            <p className="text-xs text-default-400 px-3 mb-3">
              Hier können Sie Anweisungen der KI verwalten und erweitern.
            </p>
            {loading && <p className="text-sm text-default-500 px-3">Laden…</p>}
            {error && <p className="text-sm text-danger-500 px-3">Fehler: {error}</p>}
            {prompts.map((p) => {
              const isSelected = p.id === selectedId;
              const isDirtyItem = editedById[p.id] !== undefined;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors flex items-center justify-between gap-2 ${
                    isSelected
                      ? "bg-secondaryContainer text-onSecondaryContainer font-medium"
                      : "text-onSurface hover:bg-surfaceContainer"
                  }`}
                >
                  <span className="truncate">
                    {(p.title || `Prompt ${p.id}`).replace(/\b(prompt|summary)\b/gi, (w) => w.charAt(0).toUpperCase() + w.slice(1))}
                  </span>
                  {isDirtyItem && (
                    <span className="w-2 h-2 rounded-full bg-secondary shrink-0" title="Ungespeicherte Änderungen" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="w-px bg-surfaceContainer shrink-0" />

          {/* Right Panel — selected prompt editor */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {selectedPrompt ? (
              <div className="h-full flex flex-col gap-3">
                <label className="text-sm font-medium text-default-600">
                  {selectedPrompt.title || `Prompt ${selectedPrompt.id}`}
                </label>
                <div className="flex-1 bg-white rounded-xl ps-3 pe-1 py-1">
                  <textarea
                    className="w-full h-full dark:border-default-100 focus:outline-none focus:ring-2 focus:ring-transparent resize-none"
                    spellCheck={false}
                    autoCorrect="off"
                    autoComplete="off"
                    placeholder="Geben Sie den Prompt-Inhalt ein…"
                    value={
                      editedById[selectedPrompt.id] !== undefined
                        ? editedById[selectedPrompt.id]
                        : selectedPrompt.content
                    }
                    onChange={handleChange(selectedPrompt.id)}
                  />
                </div>
              </div>
            ) : (
              !loading && (
                <div className="flex items-center justify-center h-full text-sm text-default-400">
                  Wählen Sie einen Prompt aus der Liste aus.
                </div>
              )
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Prompts;
